import {
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  IonButtons,
  IonBackButton,
  useIonToast
} from '@ionic/react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  IconButton
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Work as WorkIcon,
  Lock as LockIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { useAuthContext } from '../contexts/AuthContext';
import { updatePassword, deleteUser } from 'firebase/auth';
import { Ambassador, BaseUser, Company, Contractor, CustomField } from '../types/user';
import { activityTypeKeys, industryAndServiceKeys } from '../types/user';

export const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [presentToast] = useIonToast();
  const { userData: user, user: authUser } = useAuthContext();

  // Dialog states
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);

  interface NotificationSettings {
    likes: boolean;
    messages: boolean;
    profileVisits: boolean;
  }

  const defaultSettings: NotificationSettings = {
    likes: true,
    messages: true,
    profileVisits: true
  };

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultSettings);

  // Fetch notification settings
  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!user?.id) return;
      try {
        const settingsDoc = await getDoc(doc(db, 'notificationSettings', user.id));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setNotificationSettings({
            likes: data.likes ?? defaultSettings.likes,
            messages: data.messages ?? defaultSettings.messages,
            profileVisits: data.profileVisits ?? defaultSettings.profileVisits
          });
        } else {
          // Create default settings
          await setDoc(doc(db, 'notificationSettings', user.id), notificationSettings);
        }
      } catch (error) {
        console.error('Error fetching notification settings:', error);
      }
    };
    fetchNotificationSettings();
  }, [user?.id]);

  const handleNotificationSettingChange = async (setting: keyof typeof notificationSettings) => {
    if (!user?.id) return;
    try {
      const newSettings = {
        ...notificationSettings,
        [setting]: !notificationSettings[setting]
      };
      await updateDoc(doc(db, 'notificationSettings', user.id), newSettings);
      setNotificationSettings(newSettings);
      presentToast({
        message: t('settings.notification_settings_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      presentToast({
        message: t('settings.update_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  // Form states
  const [description, setDescription] = useState(
    user?.type === 'company'
      ? (user as Company).description
      : user?.type === 'contractor'
      ? (user as Contractor).servicesDescription
      : user?.type === 'ambassador'
      ? (user as Ambassador).ambassadorDescription
      : ''
  );
  
  const [specialization, setSpecialization] = useState(
    user?.type === 'company'
      ? (user as Company).specialization || ''
      : user?.type === 'contractor'
      ? (user as Contractor).specialization || ''
      : ''
  );

  const getCustomFieldValue = (field: CustomField | string | undefined): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.isOther ? (field.customValue || '') : field.value;
  };

  const getActivityValue = (user: BaseUser | null): CustomField => {
    if (!user) return { value: '', isOther: false, customValue: null };
    try {
      switch (user.type) {
        case 'company':
          const industry = (user as Company).industry;
          return typeof industry === 'string' 
            ? { value: industry, isOther: false, customValue: null }
            : (industry || { value: '', isOther: false, customValue: null });
        case 'contractor':
          const serviceType = (user as Contractor).serviceType;
          return typeof serviceType === 'string'
            ? { value: serviceType, isOther: false, customValue: null }
            : (serviceType || { value: '', isOther: false, customValue: null });
        case 'ambassador':
          return (user as Ambassador).activityType || { value: '', isOther: false, customValue: null };
        default:
          return { value: '', isOther: false, customValue: null };
      }
    } catch (error) {
      return { value: '', isOther: false, customValue: null };
    }
  };

  const [activity, setActivity] = useState<CustomField>(getActivityValue(user));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleUpdateProfile = async () => {
    if (!user?.id) return;

    try {
      const userRef = doc(db, 'users', user.id);
      const updateData: any = {};

      if (user.type === 'company') {
        updateData.description = description;
        updateData.industry = activity;
        updateData.specialization = specialization;
      } else if (user.type === 'contractor') {
        updateData.servicesDescription = description;
        updateData.serviceType = activity;
        updateData.specialization = specialization;
      } else if (user.type === 'ambassador') {
        updateData.ambassadorDescription = description;
        updateData.activityType = activity;
      }

      await updateDoc(userRef, updateData);
      setEditProfileOpen(false);
      presentToast({
        message: t('settings.profile_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      presentToast({
        message: t('settings.update_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const handleChangePassword = async () => {
    if (!authUser || newPassword !== confirmPassword) {
      presentToast({
        message: t('settings.passwords_dont_match'),
        duration: 2000,
        color: 'danger'
      });
      return;
    }

    try {
      await updatePassword(authUser, newPassword);
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      presentToast({
        message: t('settings.password_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error updating password:', error);
      presentToast({
        message: t('settings.password_update_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (!authUser || !user?.id) return;

    try {
      // Delete user document
      await deleteDoc(doc(db, 'users', user.id));
      // Delete auth user
      await deleteUser(authUser);
      history.replace('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      presentToast({
        message: t('settings.delete_account_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      history.replace('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
      presentToast({
        message: t('settings.logout_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my-profile" />
          </IonButtons>
          <Typography variant="h6" sx={{ ml: 2 }}>
            {t('settings.title')}
          </Typography>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <List>
          <ListItem onClick={() => setEditProfileOpen(true)}>
            <ListItemIcon>
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.edit_profile')}
              secondary={t('settings.edit_profile_desc')}
            />
          </ListItem>

          <Divider />

          <ListItem onClick={() => setPasswordDialogOpen(true)}>
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.change_password')}
              secondary={t('settings.change_password_desc')}
            />
          </ListItem>

          <Divider />

          <ListItem onClick={() => setDeleteAccountOpen(true)}>
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.delete_account')}
              secondary={t('settings.delete_account_desc')}
            />
          </ListItem>

          <Divider />

          <ListItem onClick={() => setNotificationSettingsOpen(true)}>
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.notifications')}
              secondary={t('settings.notifications_desc')}
            />
          </ListItem>

          <Divider />

          <ListItem onClick={() => setLanguageDialogOpen(true)}>
            <ListItemIcon>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.language')}
              secondary={t('settings.language_desc')}
            />
          </ListItem>

          <Divider />

          <ListItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.logout')}
              secondary={t('settings.logout_desc')}
            />
          </ListItem>
        </List>

        {/* Edit Profile Dialog */}
        <Dialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)}>
          <DialogTitle>{t('settings.edit_profile')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('settings.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2, mt: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('settings.activity')}</InputLabel>
              <Select
                value={activity.value}
                onChange={(e) => setActivity({ value: e.target.value, isOther: false, customValue: null })}
                label={t('settings.activity')}
              >
                {user?.type === 'ambassador' ? (
                  activityTypeKeys.map(key => (
                    <MenuItem key={key} value={key}>
                      {t(`registration.activityTypes.${key}`)}
                    </MenuItem>
                  ))
                ) : (
                  industryAndServiceKeys.map(key => (
                    <MenuItem key={key} value={key}>
                      {t(`registration.industries.${key}`)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            {(user?.type === 'company' || user?.type === 'contractor') && (
              <TextField
                fullWidth
                label={t('registration.specialization')}
                value={specialization}
                onChange={(e) => setSpecialization(e.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                helperText={`${specialization.length}/50`}
                sx={{ mb: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditProfileOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateProfile} variant="contained">
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
          <DialogTitle>{t('settings.change_password')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              type="password"
              label={t('settings.new_password')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2, mt: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label={t('settings.confirm_password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPasswordDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangePassword} variant="contained">
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={deleteAccountOpen} onClose={() => setDeleteAccountOpen(false)}>
          <DialogTitle>{t('settings.delete_account_confirm')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('settings.delete_account_warning')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAccountOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleDeleteAccount} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Settings Dialog */}
        <Dialog open={notificationSettingsOpen} onClose={() => setNotificationSettingsOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('settings.notifications')}
              <IconButton onClick={() => setNotificationSettingsOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <List>
              <ListItem>
                <ListItemText primary={t('settings.notify_likes')} />
                <Switch
                  edge="end"
                  checked={notificationSettings.likes}
                  onChange={() => handleNotificationSettingChange('likes')}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('settings.notify_messages')} />
                <Switch
                  edge="end"
                  checked={notificationSettings.messages}
                  onChange={() => handleNotificationSettingChange('messages')}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('settings.notify_profile_visits')} />
                <Switch
                  edge="end"
                  checked={notificationSettings.profileVisits}
                  onChange={() => handleNotificationSettingChange('profileVisits')}
                />
              </ListItem>
            </List>
          </DialogContent>
        </Dialog>

        {/* Language Selection Dialog */}
        <Dialog open={languageDialogOpen} onClose={() => setLanguageDialogOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('settings.language')}
              <IconButton onClick={() => setLanguageDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <List>
              <ListItem 
                onClick={() => {
                  i18n.changeLanguage('en');
                  setLanguageDialogOpen(false);
                  presentToast({
                    message: t('settings.language_updated'),
                    duration: 2000,
                    color: 'success'
                  });
                }}
                sx={{ 
                  bgcolor: i18n.language === 'en' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' },
                  cursor: 'pointer'
                }}
              >
                <ListItemText primary={t('language.english')} />
              </ListItem>
              <ListItem 
                onClick={() => {
                  i18n.changeLanguage('fr');
                  setLanguageDialogOpen(false);
                  presentToast({
                    message: t('settings.language_updated'),
                    duration: 2000,
                    color: 'success'
                  });
                }}
                sx={{ 
                  bgcolor: i18n.language === 'fr' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' },
                  cursor: 'pointer'
                }}
              >
                <ListItemText primary={t('language.french')} />
              </ListItem>
            </List>
          </DialogContent>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default Settings;
